import {
  Activity,
  AlertTriangle,
  Clock3,
  LoaderCircle,
  Play,
  Signal,
  Volume2,
  VolumeX,
  Wifi,
  WifiOff,
} from 'lucide-react';
import {
  startTransition,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import type { SettlementSummary } from '../../shared/engine/holdem';
import {
  normalizeRoomCode,
  type CreateRoomPayload,
  type RoomSnapshot,
  type StandardAck,
} from '../../shared/protocol';
import tableImage from '../assets/river-zero-table.webp';
import { TableScene } from './components/TableScene';
import {
  clearStoredSession,
  loadStoredSession,
  persistStoredSession,
  type StoredSession,
} from './session-storage';

type Mode = 'create' | 'join';
type ConnectionState = 'offline' | 'connecting' | 'online';

const getSocketBaseUrl = () => import.meta.env.VITE_SOCKET_URL ?? undefined;

const readAckError = <T,>(response: StandardAck<T>) => response.error ?? '请求失败';

const clampNumber = (value: number, fallback: number) => (Number.isFinite(value) ? value : fallback);

const playSound = (enabled: boolean, kind: 'deal' | 'pot' | 'turn' | 'win') => {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  const AudioCtor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) {
    return;
  }

  const audioContext = new AudioCtor();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  const now = audioContext.currentTime;
  const profile = {
    deal: [660, 880, 0.025],
    pot: [180, 240, 0.045],
    turn: [520, 740, 0.04],
    win: [440, 660, 0.12],
  }[kind];

  oscillator.type = kind === 'pot' ? 'triangle' : 'sine';
  oscillator.frequency.setValueAtTime(profile[0], now);
  oscillator.frequency.exponentialRampToValueAtTime(profile[1], now + profile[2]);
  gainNode.gain.setValueAtTime(0.0001, now);
  gainNode.gain.exponentialRampToValueAtTime(0.08, now + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, now + profile[2]);
  oscillator.start(now);
  oscillator.stop(now + profile[2] + 0.02);
  oscillator.onended = () => {
    void audioContext.close();
  };
};

const describeConnection = (state: ConnectionState) => {
  if (state === 'online') {
    return '在线';
  }
  if (state === 'connecting') {
    return '连接中';
  }
  return '离线';
};

const SettlementModal = ({
  settlement,
}: {
  settlement: SettlementSummary;
}) => (
  <div className="settlement-modal">
    <div className="settlement-modal__panel">
      <div className="settlement-modal__header">
        <div>
          <p className="kicker">结算</p>
          <h2>{settlement.reason === 'showdown' ? '摊牌结果' : '无人跟注'}</h2>
        </div>
        <div className="settlement-modal__pot">总底池 {settlement.totalPot}</div>
      </div>
      <div className="settlement-modal__board">
        {settlement.board.map((card) => (
          <span className="settlement-chip" key={card}>
            {card}
          </span>
        ))}
      </div>
      <div className="settlement-modal__players">
        {settlement.showdown.map((entry) => (
          <article className={`settlement-row${entry.won ? ' settlement-row--winner' : ''}`} key={entry.playerId}>
            <div className="settlement-row__main">
              <strong>{entry.name}</strong>
              <span>{entry.cards.join(' ') || '未亮牌'}</span>
            </div>
            <div className="settlement-row__detail">
              <span>{entry.handName}</span>
              <span>{entry.payout > 0 ? `+${entry.payout}` : '0'}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  </div>
);

export const App = () => {
  const [mode, setMode] = useState<Mode>('create');
  const [connectionState, setConnectionState] = useState<ConnectionState>('offline');
  const [errorMessage, setErrorMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [pingMs, setPingMs] = useState<number | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [betTarget, setBetTarget] = useState(40);
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [formState, setFormState] = useState({
    name: '',
    roomCode: '',
    smallBlind: 10,
    bigBlind: 20,
    startingStack: 2000,
  });
  const socketRef = useRef<Socket | null>(null);
  const previousSnapshotRef = useRef<RoomSnapshot | null>(null);
  const sessionRef = useRef<StoredSession | null>(null);

  const applySnapshot = useEffectEvent((nextSnapshot: RoomSnapshot) => {
    const previous = previousSnapshotRef.current;

    if (previous) {
      if (nextSnapshot.handNumber > previous.handNumber || nextSnapshot.communityCards.length > previous.communityCards.length) {
        playSound(soundEnabled, 'deal');
      }
      if (nextSnapshot.potTotal > previous.potTotal && nextSnapshot.street !== 'settlement') {
        playSound(soundEnabled, 'pot');
      }
      if (nextSnapshot.actionSeat === nextSnapshot.self?.seat && previous.actionSeat !== nextSnapshot.self?.seat) {
        playSound(soundEnabled, 'turn');
      }
      if (
        nextSnapshot.lastSettlement &&
        nextSnapshot.lastSettlement.handNumber !== previous.lastSettlement?.handNumber
      ) {
        playSound(soundEnabled, 'win');
      }
    }

    previousSnapshotRef.current = nextSnapshot;
    startTransition(() => {
      setSnapshot(nextSnapshot);
      setConnectionState('online');
    });
  });

  const ensureSocket = useEffectEvent(async () => {
    if (!socketRef.current) {
      const socket = io(getSocketBaseUrl(), {
        autoConnect: false,
        reconnection: true,
      });

      socket.on('connect', () => {
        setConnectionState('online');
        if (sessionRef.current) {
          socket.emit(
            'session:resume',
            {
              roomCode: sessionRef.current.roomCode,
              playerToken: sessionRef.current.playerToken,
            },
            (response: StandardAck) => {
              if (!response.ok) {
                clearStoredSession();
                sessionRef.current = null;
                setSnapshot(null);
                setErrorMessage(readAckError(response));
                return;
              }
              if (response.snapshot) {
                applySnapshot(response.snapshot);
              }
            },
          );
        }
      });

      socket.on('disconnect', () => {
        setConnectionState('offline');
      });

      socket.on('connect_error', (error) => {
        setConnectionState('offline');
        setErrorMessage(error.message || '无法连接牌局服务');
      });

      socket.on('room:snapshot', (nextSnapshot: RoomSnapshot) => {
        applySnapshot(nextSnapshot);
      });

      socketRef.current = socket;
    }

    const socket = socketRef.current;
    if (!socket.connected) {
      setConnectionState('connecting');
      await new Promise<void>((resolve, reject) => {
        socket.once('connect', () => resolve());
        socket.once('connect_error', reject);
        socket.connect();
      });
    }

    return socket;
  });

  const emitAck = useEffectEvent(
    async <T,>(eventName: string, payload: unknown): Promise<StandardAck<T>> => {
      const socket = await ensureSocket();
      return new Promise<StandardAck<T>>((resolve) => {
        socket.emit(eventName, payload, (response: StandardAck<T>) => resolve(response));
      });
    },
  );

  useEffect(() => {
    const stored = loadStoredSession();
    if (!stored) {
      return;
    }

    sessionRef.current = stored;
    setFormState((current) => ({
      ...current,
      name: stored.name,
      roomCode: stored.roomCode,
    }));

    void ensureSocket().catch(() => {
      setConnectionState('offline');
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (snapshot?.self) {
      setBetTarget(
        Math.max(
          snapshot.self.actionOptions.minRaiseTo,
          Math.min(snapshot.self.actionOptions.maxRaiseTo, snapshot.self.actionOptions.minRaiseTo),
        ),
      );
    }
  }, [snapshot]);

  useEffect(() => {
    if (connectionState !== 'online' || !socketRef.current?.connected) {
      return;
    }

    const timer = window.setInterval(() => {
      if (!socketRef.current?.connected) {
        return;
      }

      const sentAt = Date.now();
      socketRef.current.emit(
        'client:ping',
        { sentAt },
        (response: StandardAck<{ sentAt: number; serverAt: number }>) => {
          if (response.ok && response.data) {
            setPingMs(Date.now() - response.data.sentAt);
          }
        },
      );
    }, 2_500);

    return () => {
      window.clearInterval(timer);
    };
  }, [connectionState]);

  const handleCreateOrJoin = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setErrorMessage('');

    try {
      const payload =
        mode === 'create'
          ? ({
              name: formState.name || '匿名牌手',
              config: {
                smallBlind: clampNumber(formState.smallBlind, 10),
                bigBlind: clampNumber(formState.bigBlind, 20),
                startingStack: clampNumber(formState.startingStack, 2000),
              },
            } satisfies CreateRoomPayload)
          : {
              name: formState.name || '匿名牌手',
              roomCode: normalizeRoomCode(formState.roomCode),
            };

      const response =
        mode === 'create'
          ? await emitAck('lobby:create', payload)
          : await emitAck('lobby:join', payload);

      if (!response.ok || !response.playerToken || !response.playerId || !response.roomCode || !response.snapshot) {
        throw new Error(readAckError(response));
      }

      const session: StoredSession = {
        roomCode: response.roomCode,
        playerId: response.playerId,
        playerToken: response.playerToken,
        name: formState.name || '匿名牌手',
      };

      sessionRef.current = session;
      persistStoredSession(session);
      applySnapshot(response.snapshot);
      setFormState((current) => ({
        ...current,
        roomCode: response.roomCode ?? current.roomCode,
      }));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '连接失败');
    } finally {
      setPending(false);
    }
  };

  const startHandNow = async () => {
    if (!snapshot?.roomCode) {
      return;
    }

    setPending(true);
    setErrorMessage('');
    try {
      const response = await emitAck('table:start-hand', { roomCode: snapshot.roomCode });
      if (!response.ok || !response.snapshot) {
        throw new Error(readAckError(response));
      }
      applySnapshot(response.snapshot);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '开局失败');
    } finally {
      setPending(false);
    }
  };

  const sendAction = async (action: { type: string; amount?: number }) => {
    if (!snapshot?.roomCode) {
      return;
    }

    setPending(true);
    setErrorMessage('');

    try {
      const response = await emitAck('table:action', {
        roomCode: snapshot.roomCode,
        action,
      });
      if (!response.ok || !response.snapshot) {
        throw new Error(readAckError(response));
      }
      applySnapshot(response.snapshot);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '行动失败');
    } finally {
      setPending(false);
    }
  };

  const leaveSession = () => {
    socketRef.current?.disconnect();
    sessionRef.current = null;
    previousSnapshotRef.current = null;
    clearStoredSession();
    setSnapshot(null);
    setPingMs(null);
    setConnectionState('offline');
  };

  const activeSnapshot = snapshot;
  const actionOptions = activeSnapshot?.self?.actionOptions;
  const canAct = actionOptions?.canAct === true;
  const isRaiseMode = !!actionOptions && activeSnapshot.currentBet > 0;
  const streetLabel = activeSnapshot
    ? ({
        lobby: '等待开局',
        preflop: '翻牌前',
        flop: '翻牌',
        turn: '转牌',
        river: '河牌',
        showdown: '摊牌',
        settlement: '本手结算',
      } as const)[activeSnapshot.street]
    : '';

  return (
    <main className="app-shell">
      <div className="app-shell__grain" />

      <header className="top-hud">
        <div className="brand-lockup">
          <span className="brand-lockup__mark">RIVER ZERO</span>
          <span className="brand-lockup__sub">私人德州房</span>
        </div>
        <div className="status-hud">
          <div className="status-chip">
            {connectionState === 'online' ? <Wifi size={16} /> : connectionState === 'connecting' ? <LoaderCircle className="spin" size={16} /> : <WifiOff size={16} />}
            <span>{describeConnection(connectionState)}</span>
          </div>
          <div className="status-chip">
            <Signal size={16} />
            <span>{pingMs === null ? '-- ms' : `${pingMs} ms`}</span>
          </div>
          <button className="status-chip status-chip--button" onClick={() => setSoundEnabled((current) => !current)} type="button">
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            <span>{soundEnabled ? '音效开' : '音效关'}</span>
          </button>
        </div>
      </header>

      {activeSnapshot ? (
        <section className="room-stage">
          <aside className="room-sidebar">
            <div className="panel">
              <p className="kicker">房间</p>
              <h1>{activeSnapshot.roomCode}</h1>
              <div className="panel-grid">
                <span>盲注</span>
                <strong>
                  {activeSnapshot.blinds.smallBlind}/{activeSnapshot.blinds.bigBlind}
                </strong>
                <span>起始筹码</span>
                <strong>{activeSnapshot.blinds.startingStack}</strong>
                <span>在线人数</span>
                <strong>{activeSnapshot.connectedCount}</strong>
              </div>
              {activeSnapshot.self?.canStart ? (
                <button className="primary-button" onClick={startHandNow} type="button">
                  <Play size={16} />
                  <span>{activeSnapshot.handNumber === 0 ? '开始首局' : '立即发牌'}</span>
                </button>
              ) : (
                <div className="auto-note">
                  <Clock3 size={16} />
                  <span>{activeSnapshot.street === 'settlement' ? '后续牌局将自动推进' : '等待牌局流程'}</span>
                </div>
              )}
              <button className="ghost-button" onClick={leaveSession} type="button">
                离开本地会话
              </button>
            </div>
            <div className="panel panel--thin">
              <p className="kicker">行动提示</p>
              <div className="panel-grid">
                <span>当前街</span>
                <strong>{streetLabel}</strong>
                <span>到位</span>
                <strong>{activeSnapshot.actionSeat === null ? '--' : `座位 ${activeSnapshot.actionSeat + 1}`}</strong>
              </div>
              {errorMessage ? (
                <div className="error-banner">
                  <AlertTriangle size={16} />
                  <span>{errorMessage}</span>
                </div>
              ) : null}
            </div>
          </aside>

          <div className="room-main">
            <TableScene
              mode={activeSnapshot.street === 'lobby' ? 'lobby' : 'game'}
              snapshot={activeSnapshot}
              soundEnabled={soundEnabled}
            />

            {activeSnapshot.lastSettlement ? <SettlementModal settlement={activeSnapshot.lastSettlement} /> : null}

            <div className="action-dock">
              <div className="action-dock__header">
                <div>
                  <p className="kicker">行动</p>
                  <strong>
                    {canAct
                      ? '轮到你'
                      : activeSnapshot.street === 'lobby'
                        ? '等待开局'
                        : activeSnapshot.street === 'settlement'
                          ? '本手已结算'
                          : '等待其他牌手'}
                  </strong>
                </div>
                {canAct ? (
                  <span className="action-dock__callout">
                    {actionOptions.callAmount ? `跟注 ${actionOptions.callAmount}` : '可以过牌'}
                  </span>
                ) : null}
              </div>

              {actionOptions && canAct ? (
                <>
                  <div className="action-row">
                    <button className="danger-button" disabled={!actionOptions.canFold || pending} onClick={() => void sendAction({ type: 'fold' })} type="button">
                      弃牌
                    </button>
                    <button
                      className="secondary-button"
                      disabled={(!actionOptions.canCheck && !actionOptions.canCall) || pending}
                      onClick={() =>
                        void sendAction({
                          type: actionOptions.canCheck ? 'check' : 'call',
                        })
                      }
                      type="button"
                    >
                      {actionOptions.canCheck ? '过牌' : `跟注 ${actionOptions.callAmount}`}
                    </button>
                    <button className="secondary-button" disabled={!actionOptions.canAct || pending} onClick={() => void sendAction({ type: 'all-in' })} type="button">
                      全下
                    </button>
                  </div>
                  <label className="range-field">
                    <span>{isRaiseMode ? '加注到' : '下注额'}</span>
                    <input
                      max={Math.max(actionOptions.maxRaiseTo, actionOptions.minRaiseTo)}
                      min={actionOptions.minRaiseTo}
                      onChange={(event) => setBetTarget(Number(event.target.value))}
                      type="range"
                      value={Math.min(Math.max(betTarget, actionOptions.minRaiseTo), Math.max(actionOptions.maxRaiseTo, actionOptions.minRaiseTo))}
                    />
                  </label>
                  <div className="amount-row">
                    <input
                      className="amount-input"
                      max={Math.max(actionOptions.maxRaiseTo, actionOptions.minRaiseTo)}
                      min={actionOptions.minRaiseTo}
                      onChange={(event) => setBetTarget(Number(event.target.value))}
                      type="number"
                      value={betTarget}
                    />
                    <button
                      className="primary-button"
                      disabled={!actionOptions.canAct || actionOptions.maxRaiseTo < actionOptions.minRaiseTo || pending}
                      onClick={() =>
                        void sendAction({
                          type: isRaiseMode ? 'raise' : 'bet',
                          amount: betTarget,
                        })
                      }
                      type="button"
                    >
                      {isRaiseMode ? '提交加注' : '提交下注'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="auto-note">
                  <Activity size={16} />
                  <span>
                    {activeSnapshot.street === 'lobby'
                      ? '至少两位牌手在线后，由房主开始首局'
                      : activeSnapshot.street === 'settlement'
                        ? '下一手将自动开始'
                        : '牌局推进后会自动轮到你'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className="landing-shell">
          <div className="landing-stage">
            <img alt="RIVER ZERO 牌桌" className="landing-stage__image" src={tableImage} />
            <div className="landing-stage__veil" />
            <div className="landing-panel">
              <p className="kicker">原生实时牌局</p>
              <h1>RIVER ZERO</h1>
              <p className="landing-copy">
                暗炭金属与深绿台呢的私人牌室。创建四位房间码，邀请好友，即刻开始。
              </p>
              <div className="segmented-control" role="tablist" aria-label="房间模式">
                <button
                  aria-pressed={mode === 'create'}
                  className={mode === 'create' ? 'segmented-control__item segmented-control__item--active' : 'segmented-control__item'}
                  onClick={() => setMode('create')}
                  type="button"
                >
                  创建房间
                </button>
                <button
                  aria-pressed={mode === 'join'}
                  className={mode === 'join' ? 'segmented-control__item segmented-control__item--active' : 'segmented-control__item'}
                  onClick={() => setMode('join')}
                  type="button"
                >
                  加入房间
                </button>
              </div>

              <form className="landing-form" onSubmit={(event) => void handleCreateOrJoin(event)}>
                <label className="field">
                  <span>昵称</span>
                  <input
                    onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                    placeholder="例如 南风"
                    type="text"
                    value={formState.name}
                  />
                </label>

                {mode === 'join' ? (
                  <label className="field">
                    <span>房间码</span>
                    <input
                      inputMode="text"
                      maxLength={4}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          roomCode: normalizeRoomCode(event.target.value),
                        }))
                      }
                      placeholder="AB12"
                      type="text"
                      value={formState.roomCode}
                    />
                  </label>
                ) : (
                  <div className="field-grid">
                    <label className="field">
                      <span>小盲</span>
                      <input
                        min={1}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            smallBlind: Number(event.target.value),
                          }))
                        }
                        type="number"
                        value={formState.smallBlind}
                      />
                    </label>
                    <label className="field">
                      <span>大盲</span>
                      <input
                        min={2}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            bigBlind: Number(event.target.value),
                          }))
                        }
                        type="number"
                        value={formState.bigBlind}
                      />
                    </label>
                    <label className="field field--wide">
                      <span>起始筹码</span>
                      <input
                        min={40}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            startingStack: Number(event.target.value),
                          }))
                        }
                        type="number"
                        value={formState.startingStack}
                      />
                    </label>
                  </div>
                )}

                {errorMessage ? (
                  <div className="error-banner">
                    <AlertTriangle size={16} />
                    <span>{errorMessage}</span>
                  </div>
                ) : null}

                <button className="primary-button primary-button--full" disabled={pending} type="submit">
                  {pending ? <LoaderCircle className="spin" size={16} /> : <Play size={16} />}
                  <span>{mode === 'create' ? '创建并入座' : '加入牌局'}</span>
                </button>
              </form>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};
